import { MULTI_FIELD_TYPE_NAMES } from './constants';

export const checkIsMulti = (fieldTypeList, field) => {
  const fieldType = fieldTypeList.find((type) => type.id === field.field_type_id);
  if (!fieldType) {
    console.warn(`Field type with ID ${field.field_type_id} not found.`);
    return false;
  }
  return MULTI_FIELD_TYPE_NAMES.includes(fieldType.name);
}

export const defaultMatrixConfigFieldCheck = (value) => {
  const isArray = Array.isArray(value);
  return isArray ? !!value.length : !!value;
}

export const matrixConfigurationPropValueCheck = (configuration) => {
  // Allow empty configuration:
  if (!configuration) return true;

  // If configuration is set check the related field with the configuration value:
  const configurationValues = configuration?.getData?.values || [];
  return Array.isArray(configurationValues) ? !!configurationValues.length : !!configurationValues;
}

// TODO provide keys map for fields comparison
/**
 * Compares two configuration values to determine if they are equivalent.
 * This function handles both single values and arrays of values, including objects.
 */
const compareConfigurationValues = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item) => {
      if (typeof item === 'object' && item) {
        return b.some((bItem) => bItem?.id === item.id);
      } else {
        return b.includes(item);
      }
    });
  }
  if (typeof a === 'object' && a && typeof b === 'object' && b) {
    return a.id === b.id;
  }
  return a === b;
}

const getConfigurationValues = (configuration) => {
  // eslint-disable-next-line no-unused-vars
  const { id, ...fields } = configuration?.getData || {};
  return Object.values(fields);
}

/**
 * Compares two configurations to determine if they are equivalent.
 * This function checks the values of the configurations, ignoring the ID.
 * @param {Object} configurationA - The first configuration object to compare.
 * @param {Object} configurationB - The second configuration object to compare.
 * @returns {boolean} - Returns true if the configurations are equivalent, false otherwise.
 */
const compareConfigurations = (configurationA, configurationB) => {
  if (!configurationA || !configurationB) return false;
  const valuesA = getConfigurationValues(configurationA);
  const valuesB = getConfigurationValues(configurationB);

  return valuesA.length === valuesB.length && valuesA.every((value, index) => compareConfigurationValues(value, valuesB[index]));
}

/**
 * Generates a list of validators for matrix configuration.
 * Each validator checks a specific condition and returns an error message if the condition is not met.
 * @param {Object} matrix - The matrix object containing matrix data.
 * @return {Array} - An array of validator objects, each containing an ID, a check function, an error message, and a translation key.
 */
export const getMatrixConfigurationValidators = (matrix) => [
  {
    id: "access_and_assignment",
    check: (currentMatrixConfigItem) => defaultMatrixConfigFieldCheck(currentMatrixConfigItem?.getData?.action),
    message: "Please provide access and assignment values",
    tKey: "wam.validation.access_and_assignment",
  },
  {
    id: "required_values",
    check: (currentMatrixConfigItem) => !!currentMatrixConfigItem?.isValid,
    message: "Please select missing value(s)",
    tKey: "wam.validation.missing_values",
  },
  {
    id: "configuration_value",
    check: (currentMatrixConfigItem) => !!currentMatrixConfigItem?._isConfigurationValid,
    message: "Please select missing value(s)",
    tKey: "wam.validation.missing_values",
  },
  {
    id: "duplicate_rule",
    check: (currentMatrixConfigItem) => {
      const matrixConfig = matrix?.data?.config || [];
      return !matrixConfig.some((existing) => {
        if (existing === currentMatrixConfigItem) return false; // Skip self-comparison
        return compareConfigurations(existing, currentMatrixConfigItem);
      });
    },
    message: "Matix with same currentMatrixConfigItem already exists",
    tKey: "wam.validation.duplicate_rule",
  }
]